import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { LoginService } from '../login-service/login.service';
import { ToastService } from '../toast/toast.service';
import { DataShareService } from '../data_share/data-share.service';
import { LoadingServiceService } from '../loadingService/loading-service.service';
import { filter, finalize, take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
 
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();
  private sessionCheckInterval: any;
  private sessionTimeout = 30 * 60 * 1000; // 30 minutes in miliseconds
  private readonly _destroying$ = new Subject<void>();
  private isProcessingAuthentication = false;
 
  private redirectURL = environment.msalConfig.auth.redirectUri;
 
  constructor(
    private msalService: MsalService,
    private router: Router,
    private loginService: LoginService,
    private toastService: ToastService,
    private dataSharingService: DataShareService,
    public loadService: LoadingServiceService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    const storage = this.getSessionStorage();
    const isLoggedIn = storage?.getItem('isLoggedIn') === 'true';
    this.isLoggedInSubject.next(isLoggedIn); // Restore login state
    this.startSessionTimer();
  }
 
  // Update the login state and persist it in sessionStorage
  private updateLoginState(isLoggedIn: boolean): void {
    this.isLoggedInSubject.next(isLoggedIn);
    const storage = this.getSessionStorage();
    if (storage) {
      storage.setItem('isLoggedIn', isLoggedIn.toString());
    }
  }
 
  private getSessionStorage(): Storage | null | undefined {
    if (isPlatformBrowser(this.platformId)) {
      return sessionStorage;
    }
    return null;
  }
 
  private startSessionTimer(): void {
    if (!isPlatformBrowser(this.platformId)) return;
 
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
 
    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionValidity();
    }, 60000);
  }
 
  private checkSessionValidity(): void {
    const storage = this.getSessionStorage();
    if (!storage) return;
 
    const sessionTimestamp = storage.getItem('sessionTimestamp');
    if (!sessionTimestamp) return;
 
    const currentTime = new Date().getTime();
    const sessionTime = parseInt(sessionTimestamp, 10);
 
    if (currentTime - sessionTime > this.sessionTimeout) {
      this.toastService.showToast(
        'Your session has expired. Please login again',
        { classname: 'bg-waring text-dark' }
      );
      this.logout();
    }
  }
 
  // This method should NOT immediately redirect to dashboard
  public handleRedirectCallback(): void {
    if (this.isProcessingAuthentication) return;
 
    this.isProcessingAuthentication = true;
    this.msalService
      .handleRedirectObservable()
      .pipe(
        takeUntil(this._destroying$),
        filter((result) => !!result),
        take(1)
      )
      .subscribe({
        next: (result: AuthenticationResult) => {
          if (result) {
            // console.log("Node during Authentication Selected : ", selectedNode)
            // Instead of redirecting, call the SSO API
            this.processAuthentication(result);
            this.router.navigate(['/dashboardpage']);
          }
        },
        error: (error) => {
          //console.log('MSAL Redirect Error:', error);
          this.toastService.showToast(`MSAL Redirect error: ${error}`, {
            classname: 'bg-danger text-light',
          });
          this.router.navigate(['/loginpage']);
        },
      });
  }
 
  public processAuthentication(result: AuthenticationResult): void {
    // console.log("Authentication result:", result);
    this.loadService.show();
    const storage = this.getSessionStorage();
    if (!storage) return;
 
    const selectedNode =
      this.dataSharingService.getCurrentNodeValue() ||
      storage.getItem('selectedNode');
    const selectedEnv = storage.getItem('selectedEnv');
    // console.log("Node during Authentication Selected : ", selectedNode)
 
    // this.loginService.getLoginDetails(result.account.username, result.accessToken, this.redirectURL, selectedNode, selectedEnv)
    // In auth.service.ts, within processAuthentication()
    this.loginService
      .getLoginDetails(
        result.account.username,
        result.accessToken,
        this.redirectURL,
        selectedNode ?? undefined, // Convert null to undefined
        selectedEnv ?? undefined // Convert null to undefined
      )
      .pipe(
        takeUntil(this._destroying$),
        finalize(() => this.loadService.hide())
      )
      .subscribe({
        next: (response: any) => {
          // console.log("Login API response:", response);
          this.toastService.showToast(
            `Login Successful, Welcome: ${result.account.username}`,
            { classname: 'bg-success text-light' }
          );
 
          // this.isLoggedInSubject.next(true);
          // sessionStorage.setItem('isLoggedIn', 'true'); // Persist login state
          this.updateLoginState(true);
 
          // Store the account information
          this.msalService.instance.setActiveAccount(result.account);
          this.dataSharingService.updateUserDetails(response);
          //const sessionID = response.session_id
          storage.setItem('Session_Id', response.session_id);
          storage.setItem('sessionTimestamp', new Date().getTime().toString());
 
          this.startSessionTimer();
          // Only redirect after successful Snowflake SSO
          this.router.navigate(['/dashboardpage']);
        },
        error: (error: any) => {
          this.updateLoginState(false);
          this.toastService.showToast(
            `Snowflake SSO Login Failed: ${error.error.detail}`,
            { classname: 'bg-danger text-light' }
          );
          // Sign out from Azure AD since Snowflake SSO failed
          // this.msalService.logout();
          this.router.navigate(['/loginpage']);
        },
      });
  }
 
  // Call this method during logout
  public logout(): void {
    this.updateLoginState(false); // Set login state to false
    const storage = this.getSessionStorage();
    if (!storage) return;
 
    const sessionID = storage.getItem('Session_Id');
    if (sessionID) {
      this.loginService.appLogOut({ session_id: sessionID }).subscribe({
        next: () => {
          this.clearSession();
          this.msalService.logoutRedirect({
            postLogoutRedirectUri: window.location.origin + '/loginpage',
          });
        },
        error: (error) => {
          console.error('Logout API failed', error);
          this.clearSession();
          this.msalService.logoutRedirect({
            postLogoutRedirectUri: window.location.origin + '/loginpage',
          });
        },
      });
    } else {
      this.clearSession();
      this.msalService.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/loginpage',
      });
    }
  }
 
  public clearSession(): void {
    const storage = this.getSessionStorage();
    if (!storage) return;
 
    storage.removeItem('Session_Id');
    storage.removeItem('sessionTimestamp');
    storage.removeItem('selectedNode');
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
  }
 
  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    // if (this.sessionCheckInterval) {
    //   clearInterval(this.sessionCheckInterval);
    // }
  }
}